import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import {
  AdminOrganization,
  OrganizationQuotaDetails,
  Project,
  Table,
  OrganizationMember,
  Plan,
} from '../../../types/domain';

export function useAdminOrgData() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser, currentProject, setCurrentProject } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<AdminOrganization | null>(null);
  const [quota, setQuota] = useState<OrganizationQuotaDetails | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [activeTab, setActiveTab] = useState<'projects' | 'members' | 'quotas' | 'danger'>('projects');

  const [copiedId, setCopiedId] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Project expansion & datastores state
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [projectTables, setProjectTables] = useState<Record<string, Table[]>>({});
  const [loadingTables, setLoadingTables] = useState<Record<string, boolean>>({});

  // Quota edit slide-over drawer state
  const [editQuotaDrawerOpen, setEditQuotaDrawerOpen] = useState(false);
  const [editTier, setEditTier] = useState('free');
  const [editMaxSubmissions, setEditMaxSubmissions] = useState(500);
  const [editMaxTables, setEditMaxTables] = useState(3);
  const [editMaxSeats, setEditMaxSeats] = useState(2);
  const [isUpdatingQuotas, setIsUpdatingQuotas] = useState(false);

  // In-page deletion states
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [projectDeleteInput, setProjectDeleteInput] = useState('');
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  const [tableToDelete, setTableToDelete] = useState<{ projectId: string; table: Table } | null>(null);
  const [tableDeleteInput, setTableDeleteInput] = useState('');
  const [isDeletingTable, setIsDeletingTable] = useState(false);

  const [orgDeleteInput, setOrgDeleteInput] = useState('');
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);

  // Superadmin Guard
  useEffect(() => {
    if (currentUser && !currentUser.is_superadmin) {
      showToast({
        type: 'error',
        title: 'Unauthorized Access',
        message: 'Platform administrator permissions are required.',
      });
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate, showToast]);

  const fetchOrgData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [res, plansRes] = await Promise.all([
        apiClient<{
          organization: AdminOrganization;
          quota?: OrganizationQuotaDetails;
          projects?: Project[];
          members?: OrganizationMember[];
        }>(`/admin/organizations/${id}`),
        apiClient<{ data: Plan[] }>('/plans').catch(() => ({ data: [] })),
      ]);

      const org = res.organization;
      setOrganization(org);
      setQuota(res.quota || null);
      setProjects(res.projects || []);
      setMembers(res.members || []);
      setPlans(plansRes.data || []);

      setEditTier(org.plan_tier?.toLowerCase() || 'free');
      setEditMaxSubmissions(org.max_monthly_submissions || 500);
      setEditMaxTables(org.max_tables || 3);
      setEditMaxSeats(org.max_team_seats || 2);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to load organization',
        message: err?.message || 'Could not retrieve organization details',
      });
      navigate('/admin/organizations', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  const toggleProjectExpansion = async (projectId: string) => {
    const isExpanded = !!expandedProjects[projectId];
    if (isExpanded) {
      setExpandedProjects((prev) => ({ ...prev, [projectId]: false }));
      return;
    }

    setExpandedProjects((prev) => ({ ...prev, [projectId]: true }));
    if (projectTables[projectId]) return;

    setLoadingTables((prev) => ({ ...prev, [projectId]: true }));
    try {
      const res = await apiClient<{ data: Table[] }>(`/admin/projects/${projectId}/tables`);
      setProjectTables((prev) => ({ ...prev, [projectId]: res.data || [] }));
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to load datastores',
        message: err?.message || 'Could not fetch datastores for this project',
      });
    } finally {
      setLoadingTables((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const handleCopyId = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleTierSelect = (newTier: string) => {
    setEditTier(newTier);
    const matched = plans.find((p) => p.slug.toLowerCase() === newTier.toLowerCase());
    if (matched) {
      setEditMaxSubmissions(matched.max_monthly_submissions);
      setEditMaxTables(matched.max_tables);
      setEditMaxSeats(matched.max_team_seats);
    }
  };

  const handleSaveQuotas = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;
    setIsUpdatingQuotas(true);
    try {
      await apiClient(`/admin/organizations/${organization.id}/tier`, {
        method: 'PUT',
        body: JSON.stringify({
          plan_tier: editTier,
          max_monthly_submissions: editMaxSubmissions,
          max_tables: editMaxTables,
          max_team_seats: editMaxSeats,
        }),
      });

      showToast({
        type: 'success',
        title: 'Quotas & Plan Updated',
        message: `${organization.name} updated to ${editTier.toUpperCase()} tier.`,
      });

      setOrganization((prev) =>
        prev
          ? {
              ...prev,
              plan_tier: editTier,
              max_monthly_submissions: editMaxSubmissions,
              max_tables: editMaxTables,
              max_team_seats: editMaxSeats,
            }
          : null
      );
      setEditQuotaDrawerOpen(false);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update failed',
        message: err?.message || 'Failed to update organization quotas',
      });
    } finally {
      setIsUpdatingQuotas(false);
    }
  };

  const handleConfirmDeleteProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectToDelete || projectDeleteInput !== projectToDelete.slug) return;
    setIsDeletingProject(true);
    try {
      await apiClient(`/admin/projects/${projectToDelete.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Project Deleted',
        message: `Project "${projectToDelete.name}" and all associated datastores and storage files were wiped.`,
      });

      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      if (currentProject && currentProject.id === projectToDelete.id) {
        setCurrentProject(null);
      }
      setProjectToDelete(null);
      setProjectDeleteInput('');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete project',
      });
    } finally {
      setIsDeletingProject(false);
    }
  };

  const handleConfirmDeleteTable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tableToDelete || tableDeleteInput !== tableToDelete.table.slug) return;
    setIsDeletingTable(true);
    try {
      await apiClient(`/admin/projects/${tableToDelete.projectId}/tables/${tableToDelete.table.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Datastore Deleted',
        message: `Datastore "${tableToDelete.table.name}" and all rows and uploaded files were deleted.`,
      });

      setProjectTables((prev) => ({
        ...prev,
        [tableToDelete.projectId]: (prev[tableToDelete.projectId] || []).filter(
          (t) => t.id !== tableToDelete.table.id
        ),
      }));
      setProjects((prev) =>
        prev.map((p) =>
          p.id === tableToDelete.projectId
            ? { ...p, table_count: Math.max(0, (p.table_count || 1) - 1) }
            : p
        )
      );
      setTableToDelete(null);
      setTableDeleteInput('');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete datastore',
      });
    } finally {
      setIsDeletingTable(false);
    }
  };

  const handleConfirmDeleteOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization || orgDeleteInput !== organization.slug) return;
    setIsDeletingOrg(true);
    try {
      await apiClient(`/admin/organizations/${organization.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Organization Purged',
        message: `Organization "${organization.name}", all projects, datastores, submissions, and storage files were deleted.`,
      });

      navigate('/admin/organizations', { replace: true });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Organization Deletion Failed',
        message: err?.message || 'Could not delete organization',
      });
      setIsDeletingOrg(false);
    }
  };

  const handleRemoveMember = async (member: OrganizationMember) => {
    if (!organization) return;
    const memberId = member.user_id || member.id;
    const memberEmail = member.email || member.user_id;
    if (!confirm(`Are you sure you want to remove ${memberEmail} from ${organization.name}?`)) {
      return;
    }
    try {
      await apiClient(`/admin/organizations/${organization.id}/members/${memberId}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Member Removed',
        message: 'The user was removed from this organization.',
      });
      setMembers((prev) => prev.filter((m) => (m.user_id || m.id) !== memberId));
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Remove Failed',
        message: err?.message || 'Could not remove member',
      });
    }
  };

  return {
    id,
    isLoading,
    organization,
    quota,
    projects,
    members,
    plans,
    activeTab,
    setActiveTab,
    copiedId,
    copiedToken,
    expandedProjects,
    projectTables,
    loadingTables,
    editQuotaDrawerOpen,
    setEditQuotaDrawerOpen,
    editTier,
    editMaxSubmissions,
    setEditMaxSubmissions,
    editMaxTables,
    setEditMaxTables,
    editMaxSeats,
    setEditMaxSeats,
    isUpdatingQuotas,
    projectToDelete,
    setProjectToDelete,
    projectDeleteInput,
    setProjectDeleteInput,
    isDeletingProject,
    tableToDelete,
    setTableToDelete,
    tableDeleteInput,
    setTableDeleteInput,
    isDeletingTable,
    orgDeleteInput,
    setOrgDeleteInput,
    isDeletingOrg,
    toggleProjectExpansion,
    handleCopyId,
    handleCopyToken,
    handleTierSelect,
    handleSaveQuotas,
    handleConfirmDeleteProject,
    handleConfirmDeleteTable,
    handleConfirmDeleteOrg,
    handleRemoveMember,
  };
}
